import React, { useRef, useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";

const StackedBarChart = ({ t, chartData, title }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const chartContainerRef = useRef(null);
  const downloadRef = useRef(null);

  const options = {
    chart: {
      id: "stacked-bar-chart",
      type: "bar",
      stacked: true,
      toolbar: { show: false },
    },
    xaxis: {
      categories: chartData?.categories || [],
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        dataLabels: { 
          position: "center",  // Show labels in center of each segment
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function(val) {
        return val > 0 ? val : '';  // Only show label if value > 0
      },
      style: {
        fontSize: '11px',
        fontWeight: 'bold',
        colors: ['#fff']  // White text for visibility on colored bars
      },
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        opacity: 0.5
      }
    },
    colors: ["#1E40AF", "#16A34A", "#D97706", "#6B7280"],
    legend: { 
      show: true,
      position: "bottom",
    },
    tooltip: {
      y: {
        formatter: (val) => val,
      },
    },
  };


  // Download chart as PNG using html2canvas
  const handleDownloadPNG = async () => {
    if (chartContainerRef.current) {
      const canvas = await html2canvas(chartContainerRef.current);
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${t(title)}.png`;
      link.click();
      setMenuOpen(false);
    }
  };

  // Download chart as SVG
  const handleDownloadSVG = () => {
    const svg = chartContainerRef.current?.querySelector("svg");
    if (svg) {
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svg);
      const width = svg.getAttribute("width") || 600;
      const height = svg.getAttribute("height") || 400;
      const titleText = t(title);

      const titleSvg = `
        <svg width="${width}" height="${parseInt(height) + 40}" xmlns="http://www.w3.org/2000/svg">
          <text x="20" y="30" font-size="22" font-family="Arial" fill="#222">${titleText}</text>
          <g transform="translate(0,40)">
            ${svgString}
          </g>
        </svg>
      `;

      const blob = new Blob([titleSvg], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${titleText}.svg`;
      a.click();
      URL.revokeObjectURL(url);
      setMenuOpen(false);
    }
  };

  // Download data as CSV
  const handleDownloadCSV = () => {
    let csv = `"${t(title)}"\n\n`;
    csv += ["Category", ...(chartData?.series?.map(s => s.name) || [])].join(",") + "\n";
    (chartData?.categories || []).forEach((cat, i) => {
      const row = [cat, ...(chartData?.series?.map(s => s.data[i]) || [])];
      csv += row.join(",") + "\n";
    });
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t(title)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setMenuOpen(false);
  };

  // Download data as Excel
  const handleDownloadExcel = () => {
    const header = [t("category"), ...(chartData?.series?.map(s => s.name) || [])];
    const rows = (chartData?.categories || []).map((cat, i) => 
      [cat, ...(chartData?.series?.map(s => s.data[i]) || [])]
    );
    const wsData = [header, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t(title).substring(0, 31));
    XLSX.writeFile(wb, `${t(title)}.xlsx`);
    setMenuOpen(false);
  };

  // Close menu on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  return (
    <div style={{ position: "relative" }}>
      {/* Custom menu button */}
      <div ref={downloadRef} style={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}>
        <div
          style={{
            width: 28,
            height: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: 4,
            background: menuOpen ? "#f3f4f6" : "transparent",
            border: menuOpen ? "1px solid #ccc" : "none"
          }}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ width: 15, height: 1.5, background: "#333", borderRadius: 2 }} />
            <span style={{ width: 15, height: 1.5, background: "#333", borderRadius: 2 }} />
            <span style={{ width: 15, height: 1.5, background: "#333", borderRadius: 2 }} />
          </div>
        </div>
        {menuOpen && (
          <div
            style={{
              position: "absolute",
              top: 30,
              right: 0,
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: 4,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              minWidth: 140,
              zIndex: 10,
            }}
          >
            <div style={{ padding: "8px 0" }}>
              <div
                style={{ padding: "6px 18px", cursor: "pointer", whiteSpace: "nowrap" }}
                onClick={handleDownloadSVG}
              >
                Download SVG
              </div>
              <div
                style={{ padding: "6px 18px", cursor: "pointer", whiteSpace: "nowrap" }}
                onClick={handleDownloadPNG}
              >
                Download PNG
              </div>
              <div
                style={{ padding: "6px 18px", cursor: "pointer", whiteSpace: "nowrap" }}
                onClick={handleDownloadCSV}
              >
                Download CSV
              </div>
              <div
                style={{ padding: "6px 18px", cursor: "pointer", whiteSpace: "nowrap" }}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </div>
            </div>
          </div>
        )}
      </div>

      <div ref={chartContainerRef}>
        <h3 style={{ paddingLeft: "10px" }}>{t(title)}</h3>
        <div className="chart-area">
          <Chart 
            options={options} 
            series={chartData?.series || []} 
            type="bar" 
            height={300} 
          />
        </div>
      </div>
    </div>
  );
};

export default StackedBarChart;
